import type {
  MarketType,
  ExportRequest,
  ImportOptions,
  ImportReport,
  PortableDocument,
  BacktestResult,
  RunDetail,
  RunSummary,
  Candle,
  IndicatorCatalogEntry,
  IndicatorDef,
  IndicatorSeries,
  Paginated,
  StrategySpec,
} from "@strat/format";
import type {
  BenchmarkRequest,
  BenchmarkResponse,
  Bot,
  BotCommand,
  BotDetailResponse,
  BotHistoryResponse,
  BotListResponse,
  BotVerifyResponse,
  EventQuery,
  EventsResponse,
  SystemResponse,
  DeployBotRequest,
  StrategyFacets,
  StrategySummary,
  SweepRequest,
  SweepResponse,
  WalkForwardRequest,
  WalkForwardResponse,
  RobustnessRequest,
  RobustnessResponse,
  UserPrefs,
} from "@strat/api";

export interface InventoryRow {
  symbol: string;
  timeframe: string;
  marketType: MarketType;
  candleCount: number;
  minTime: number;
  maxTime: number;
}

export interface DownloadJob {
  id: string;
  symbol: string;
  timeframe: string;
  marketType: MarketType;
  from: number;
  to: number;
  fetched: number;
  total: number;
  status: "running" | "done" | "error" | "cancelled";
  error?: string;
  startedAt: number;
  finishedAt?: number;
}

export interface BacktestArgs {
  strategyId?: string;
  strategy?: StrategySpec;
  /** Strategy logic is market-agnostic — every run states what to test it against. */
  symbol: string;
  timeframe: string;
  marketType: MarketType;
  from?: number;
  to?: number;
  initialCapital: number;
  /** Measure against this symbol instead of the traded asset. */
  benchmarkSymbol?: string | null;
  benchmarkMarketType?: MarketType | null;
  /** Bars computed before the window so indicators start converged. */
  warmupBars?: number | null;
}

export interface PageParams {
  limit?: number;
  offset?: number;
  sort?: string;
  order?: "asc" | "desc";
}

export interface StrategyListParams extends PageParams {
  indicatorKind?: string;
  search?: string;
}

export interface RunListParams extends PageParams {
  symbol?: string;
  timeframe?: string;
  marketType?: MarketType;
  strategyId?: string;
  batchLabel?: string;
}

/**
 * Every data access goes through this interface. Today it's HTTP against the Node
 * server; an Electron build can swap in an IPC implementation of the same shape
 * without touching a single component.
 */
export interface IDataService {
  getSymbols(marketType: MarketType): Promise<string[]>;
  getInventory(): Promise<InventoryRow[]>;
  startDownload(
    symbol: string,
    timeframe: string,
    marketType: MarketType,
    from: number,
    to: number,
  ): Promise<{ jobId: string }>;
  listDownloads(): Promise<DownloadJob[]>;
  cancelDownload(jobId: string): Promise<void>;
  /** Resolves with how many candles were removed; rejects if there was no such dataset. */
  deleteDataset(symbol: string, timeframe: string, marketType: MarketType): Promise<{ deleted: number }>;
  getCandles(
    symbol: string,
    timeframe: string,
    marketType: MarketType,
    from?: number,
    to?: number,
  ): Promise<Candle[]>;
  getIndicatorCatalog(): Promise<IndicatorCatalogEntry[]>;
  getIndicatorSeries(
    symbol: string,
    timeframe: string,
    marketType: MarketType,
    defs: IndicatorDef[],
    from?: number,
    to?: number,
  ): Promise<IndicatorSeries[]>;
  listStrategies(params?: StrategyListParams): Promise<Paginated<StrategySummary>>;
  getStrategyFacets(): Promise<StrategyFacets>;
  getStrategy(id: string): Promise<StrategySpec>;
  saveStrategy(spec: Partial<StrategySpec>): Promise<StrategySpec>;
  deleteStrategy(id: string): Promise<void>;
  /** Computes a result without persisting anything — the live, as-you-type preview. */
  previewBacktest(args: BacktestArgs): Promise<BacktestResult>;
  /** Persists the strategy AND the run in one call — the explicit "Save" action. */
  saveBacktest(args: BacktestArgs): Promise<RunDetail>;
  /** Runs many saved strategies against one shared symbol/timeframe/period/capital. */
  runBenchmark(args: BenchmarkRequest): Promise<BenchmarkResponse>;
  /** Grid-searches one strategy's settings. Nothing is persisted. */
  runSweep(args: SweepRequest): Promise<SweepResponse>;
  /** Sweeps on a training window, then scores the leaders on held-out data. */
  runWalkForward(args: WalkForwardRequest): Promise<WalkForwardResponse>;
  /** Runs one strategy across every downloaded market. */
  runRobustness(args: RobustnessRequest): Promise<RobustnessResponse>;
  listRuns(params?: RunListParams): Promise<Paginated<RunSummary>>;
  getRun(id: string): Promise<RunDetail>;
  rerunRun(id: string): Promise<RunDetail>;
  deleteRun(id: string): Promise<void>;
  /** Builds a portable file from ids already stored. See docs/FILE_FORMAT.md. */
  exportItems(request: ExportRequest): Promise<PortableDocument>;
  /** Writes a portable file's contents in. The server re-validates it. */
  importDocument(document: unknown, options?: ImportOptions): Promise<ImportReport>;

  /** Display, chart, dashboard and table preferences — per account on the hub, per machine on the desktop. */
  getPreferences(): Promise<UserPrefs>;
  savePreferences(prefs: UserPrefs): Promise<UserPrefs>;

  // --- live paper bots ---------------------------------------------------------
  listBots(): Promise<BotListResponse>;
  getBot(id: string): Promise<BotDetailResponse>;
  botHistory(id: string): Promise<BotHistoryResponse>;
  deployBot(req: DeployBotRequest): Promise<Bot>;
  startBot(id: string): Promise<Bot>;
  stopBot(id: string): Promise<Bot>;
  commandBot(id: string, command: BotCommand): Promise<{ summary: unknown }>;
  deleteBot(id: string): Promise<void>;
  /** Replay the bot's live window as a backtest and compare the trades. */
  verifyBot(id: string): Promise<BotVerifyResponse>;
  listEvents(query?: EventQuery): Promise<EventsResponse>;
  getSystem(): Promise<SystemResponse>;
}

/**
 * The interface's single instance.
 *
 * Injected at boot rather than constructed here, and that is the whole seam:
 * the lab supplies one that speaks HTTP, and a desktop build supplies one that
 * speaks IPC. Every page imports `dataService` and none of them knows which.
 *
 * The prototype hard-coded `fetch` at this spot and still managed the desktop
 * port by replacing the file — which worked, and meant the two builds could
 * drift without a compiler noticing.
 */
let injected: IDataService | null = null;

export function setDataService(impl: IDataService): void {
  injected = impl;
}

export const dataService: IDataService = new Proxy({} as IDataService, {
  get(_target, prop: string) {
    if (!injected) {
      throw new Error(
        `No data service has been configured. Call setDataService() once at start-up, ` +
          `before anything reaches "${prop}".`,
      );
    }
    return Reflect.get(injected, prop) as unknown;
  },
});
